/**
 * ImageTransformService
 *
 * Builds Cloudflare Images proxy URLs for on-the-fly image resize / format
 * conversion / quality adjustment. Works against any URL that Cloudflare
 * Images can fetch — typically R2 public objects served via
 * `cdn.ballee.co/<key>`.
 *
 * URL shape (proxy form, branded):
 *   https://cdn.ballee.co/cdn-cgi/image/<options>/<key-or-url>
 *
 * Variants are pre-defined (thumbnail / gallery / hero / original) so callers
 * pick a use-case rather than hand-craft transform options.
 *
 * ⚠️  STATUS (updated 2026-06-06) — Cloudflare Image Resizing IS now wired
 * into the public dancer profile (hero + gallery) via `cdnImageUrl` /
 * `cdnImageSrcSet` / `cdnImageWidthUrl` in `cdn-image-url.service.ts`. The
 * "DEFERRED, not wired" note from the 2026-05-12 lock is obsolete.
 *
 * COST MODEL: billed per UNIQUE transformation (image × width × quality ×
 * fit × blur), counted once per calendar month; cached repeats within the
 * month are free; the counter resets monthly. Free tier = 5,000 unique
 * transforms/month, then HTTP 429 with `cf-resized: err=9422` on ALL
 * transforms until reset or an Images Paid plan ($0.50 / 1,000 over 5,000).
 * Ballee exhausted the free tier 2026-06-06 (every browser gallery image
 * 429'd site-wide). KEEP THE VARIANT COUNT LOW: prefer clean 2× width
 * steps, reuse a srcset width for the `src` fallback (see `cdnImageWidthUrl`
 * — crawlers fetch `src`, ignore `srcset`), and avoid adding new per-image
 * variants without weighing them against the monthly cap.
 *
 * For VIDEO transforms (thumbnails, adaptive bitrate, etc.) — DO NOT use
 * this service. Per the lock, video work goes through Mux (not Cloudflare
 * Stream — explicitly evaluated and not selected for migration-out cost
 * reasons).
 *
 * @see https://developers.cloudflare.com/images/transform-images/
 * @see .claude/clauses/critical-rules-video-architecture.md
 */

/** Pre-defined variants. Names map 1:1 to a use-case in the UI. */
export const ImageVariants = {
  /** 200×200 cover, q=75 — list items, avatars, search-result thumbnails. */
  THUMBNAIL: {
    width: 200,
    height: 200,
    fit: 'cover',
    quality: 75,
  } as ImageTransformOptions,
  /** 800×800 cover, q=80 — gallery cards, profile photo grid. */
  GALLERY: {
    width: 800,
    height: 800,
    fit: 'cover',
    quality: 80,
  } as ImageTransformOptions,
  /** 1600×1600 contain, q=85 — hero / full-screen views. */
  HERO: {
    width: 1600,
    height: 1600,
    fit: 'contain',
    quality: 85,
  } as ImageTransformOptions,
  /** 16px wide, q=30, max blur — tiny LQIP blur-up placeholder. */
  PLACEHOLDER: {
    width: 16,
    quality: 30,
    blur: 250,
  } as ImageTransformOptions,
  /** No transform — passthrough. */
  ORIGINAL: null as null,
} as const;

export type ImageVariantKey = keyof typeof ImageVariants;

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  /** Cloudflare Images "fit" mode. */
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
  /** 1-100. Higher = bigger files. */
  quality?: number;
  /** Output format. Default: auto (Cloudflare picks AVIF / WebP / JPEG by Accept header). */
  format?: 'auto' | 'avif' | 'webp' | 'jpeg' | 'png';
  /** Sharpening 0-10. */
  sharpen?: number;
  /** Background color for `fit: pad`. */
  background?: string;
  /** Gaussian blur radius, 1-250 (Cloudflare Images). Used for LQIP placeholders. */
  blur?: number;
}

export interface ImageTransformServiceConfig {
  /** The proxy origin, e.g. `https://media.ballee.app`. Required. */
  proxyOrigin: string;
}

export function createImageTransformServiceFromEnv(): ImageTransformService {
  const proxyOrigin = process.env.R2_PUBLIC_URL;
  if (!proxyOrigin) {
    throw new Error(
      'ImageTransformService requires R2_PUBLIC_URL (the Cloudflare-fronted hostname, e.g. https://media.ballee.app)',
    );
  }
  return new ImageTransformService({ proxyOrigin });
}

export class ImageTransformService {
  private proxyOrigin: string;

  constructor(config: ImageTransformServiceConfig) {
    this.proxyOrigin = config.proxyOrigin.replace(/\/+$/, '');
  }

  /**
   * Build a transform URL for an R2 key (or a pass-through full URL).
   *
   * @example
   * ```typescript
   * const tx = createImageTransformServiceFromEnv();
   *
   * // R2 key: media.ballee.app/cdn-cgi/image/width=200,height=200,fit=cover,quality=75/profiles/abc/headshot.jpg
   * tx.url('profiles/abc/headshot.jpg', 'THUMBNAIL');
   *
   * // Full URL passthrough (e.g. legacy CloudFront): same proxy, source URL appended
   * tx.url('https://example.com/foo.jpg', 'GALLERY');
   * ```
   */
  url(keyOrUrl: string, variant: ImageVariantKey): string {
    const opts = ImageVariants[variant];
    if (!opts) {
      // ORIGINAL — passthrough, no transform
      if (keyOrUrl.startsWith('http')) return keyOrUrl;
      return `${this.proxyOrigin}/${keyOrUrl.replace(/^\/+/, '')}`;
    }
    return this.buildUrl(keyOrUrl, opts);
  }

  /**
   * Build a transform URL with custom options. Use sparingly — prefer
   * `url(key, variant)` so the UI stays consistent.
   */
  customUrl(keyOrUrl: string, options: ImageTransformOptions): string {
    return this.buildUrl(keyOrUrl, options);
  }

  private buildUrl(keyOrUrl: string, options: ImageTransformOptions): string {
    const optsStr = this.serializeOptions(options);
    const target = keyOrUrl.startsWith('http')
      ? keyOrUrl
      : keyOrUrl.replace(/^\/+/, '');
    return `${this.proxyOrigin}/cdn-cgi/image/${optsStr}/${target}`;
  }

  private serializeOptions(opts: ImageTransformOptions): string {
    const parts: string[] = [];
    if (opts.width) parts.push(`width=${opts.width}`);
    if (opts.height) parts.push(`height=${opts.height}`);
    if (opts.fit) parts.push(`fit=${opts.fit}`);
    if (opts.quality) parts.push(`quality=${opts.quality}`);
    // Always emit a format so Cloudflare negotiates AVIF/WebP by Accept header
    // (falling back to the source format for older browsers). Omitting it made
    // every width/quality transform serve the source PNG/JPEG — e.g. a 400px
    // dancer tile was 112 KB PNG vs 13 KB AVIF with `format=auto`. Explicit
    // `opts.format` still wins.
    parts.push(`format=${opts.format ?? 'auto'}`);
    if (opts.sharpen !== undefined) parts.push(`sharpen=${opts.sharpen}`);
    if (opts.background) parts.push(`background=${opts.background}`);
    if (opts.blur !== undefined) parts.push(`blur=${opts.blur}`);
    return parts.join(',');
  }
}
